/**
 * Mix & Match Picker
 * Builds one shared color option plus separate top/bottom size selectors.
 */
(function () {
  'use strict';

  const SET_PROPERTY = '_mix_match_set';

  function initMixMatchPicker(block) {
    if (!block || block.dataset.mixMatchBound === 'true') return;
    block.dataset.mixMatchBound = 'true';

    const dataNode = block.querySelector('[data-mix-match-products]');
    if (!dataNode) return;

    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('Invalid mix match product data:', error);
      return;
    }

    const config = {
      blockId: block.dataset.blockId,
      moneyFormat: block.dataset.moneyFormat || '${{amount}}'
    };

    const state = {
      color: null,
      topProduct: null,
      bottomProduct: null,
      top: { variantId: null, price: 0 },
      bottom: { variantId: null, price: 0 }
    };

    const els = {
      colorOption: block.querySelector('[data-mix-match-color-option]'),
      colorInputs: Array.from(block.querySelectorAll('[data-mix-match-color-option] input[data-color]')),
      swatches: {
        top: Array.from(block.querySelectorAll('[data-group="top"] .wd-mix-match-picker__swatches--products .wd-mix-match-picker__swatch')),
        bottom: Array.from(block.querySelectorAll('[data-group="bottom"] .wd-mix-match-picker__swatches--products .wd-mix-match-picker__swatch'))
      },
      sizeList: {
        top: block.querySelector('[data-size-list="top"]'),
        bottom: block.querySelector('[data-size-list="bottom"]')
      }
    };

    const mainBtn = document.querySelector('.wt-product__add-to-cart_form--wrapper button[type="submit"]');
    const mainForm = document.querySelector('.wt-product__add-to-cart_form');
    const productCache = new Map();
    const imageWidths = [500, 750, 1000, 1200, 1680, 2048, 4096];

    function normalize(value) {
      return String(value || '').trim().toLowerCase();
    }

    function hasColor(product, color) {
      return (product?.colors || []).some((productColor) => normalize(productColor) === normalize(color));
    }

    function firstAvailableProduct(products) {
      return products.find((product) => product.variants?.some((variant) => variant.available)) || products[0] || null;
    }

    function cheapestVariant(product) {
      const variants = product?.variants || [];
      const available = variants.filter((variant) => variant.available);
      const source = available.length ? available : variants;
      if (!source.length) return null;
      return source.reduce((cheapest, variant) => (
        Number(variant.price) < Number(cheapest.price) ? variant : cheapest
      ), source[0]);
    }

    function lowestCombination() {
      let best = null;

      data.tops.forEach((top) => {
        const topVariant = cheapestVariant(top);
        if (!topVariant) return;

        data.bottoms.forEach((bottom) => {
          const bottomVariant = cheapestVariant(bottom);
          if (!bottomVariant) return;

          const total = Number(topVariant.price) + Number(bottomVariant.price);
          if (!best || total < best.total) {
            best = {
              top,
              bottom,
              topVariant,
              bottomVariant,
              total
            };
          }
        });
      });

      return best;
    }

    function isSizeOptionName(name) {
      const value = normalize(name);
      return value === 'size' || value === 'sizes' || value === 'מידה' || value === 'גודל';
    }

    function isColorOptionName(name) {
      const value = normalize(name);
      return value === 'color' || value === 'colour' || value === 'צבע';
    }

    function optionValueAt(variant, index) {
      return variant?.options?.[index] || variant?.[`option${index + 1}`] || '';
    }

    function cleanSizeLabel(product, variant) {
      const optionNames = product?.optionNames || [];
      const sizeIndex = optionNames.findIndex(isSizeOptionName);
      if (sizeIndex > -1) {
        const sizeValue = optionValueAt(variant, sizeIndex);
        if (normalize(sizeValue) && normalize(sizeValue) !== 'default title') return String(sizeValue).trim();
      }

      const colorValues = [
        ...(state.topProduct?.colors || []),
        ...(state.bottomProduct?.colors || []),
        state.color
      ].map(normalize);

      const candidates = (variant.options || [variant.option1, variant.option2, variant.option3])
        .map((value, index) => ({ value, name: optionNames[index] }))
        .filter(({ value }) => Boolean(value));

      const sizeOption = candidates.find((candidate) => {
        const value = normalize(candidate.value);
        return value && !isColorOptionName(candidate.name) && !colorValues.includes(value) && value !== 'default title';
      });

      return String(sizeOption?.value || variant.title || '')
        .split('/')
        .map((part) => part.trim())
        .find((part) => part && !colorValues.includes(normalize(part))) || String(variant.title || '').trim();
    }

    function variantMatchesColor(variant) {
      if (!state.color) return true;
      const selectedColor = normalize(state.color);
      return [variant.option1, variant.option2, variant.option3, variant.title]
        .filter(Boolean)
        .some((value) => normalize(value).split('/').map((part) => part.trim()).includes(selectedColor));
    }

    function uniqueSizeVariants(product) {
      const seen = new Set();
      const variants = [];
      const allVariants = product?.variants || [];
      const matchingVariants = allVariants.filter(variantMatchesColor);
      const sourceVariants = matchingVariants.length ? matchingVariants : allVariants;

      sourceVariants.forEach((variant) => {
        const size = cleanSizeLabel(product, variant);
        const key = normalize(size);
        if (!key || seen.has(key)) return;

        const sameSizeVariants = sourceVariants.filter((item) => normalize(cleanSizeLabel(product, item)) === key);
        const availableVariant = sameSizeVariants.find((item) => item.available) || sameSizeVariants[0];
        seen.add(key);
        variants.push({ variant: availableVariant, size });
      });

      return variants;
    }

    function renderSizes(side, product, preferredVariantId) {
      const list = els.sizeList[side];
      if (!list) return;

      const previousSizeLabel = list.querySelector('input:checked')?.dataset.size || null;

      list.innerHTML = '';
      state[side].variantId = null;
      state[side].price = 0;

      uniqueSizeVariants(product).forEach(({ variant, size }, index) => {
        const li = document.createElement('li');
        li.className = 'f-button__list__item';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.id = `mm-size-${side}-${config.blockId}-${index}`;
        radio.name = `mm-size-${side}-${config.blockId}`;
        radio.value = variant.id;
        radio.dataset.price = variant.price || 0;
        radio.dataset.size = size;

        if (!variant.available) {
          radio.className = 'disabled';
          radio.disabled = true;
        }

        const label = document.createElement('label');
        label.className = `f-button__list__link${variant.available ? '' : ' disabled'}`;
        label.htmlFor = radio.id;
        label.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        label.textContent = size;

        li.append(radio, label);
        list.appendChild(li);
      });

      const preferredRadio = preferredVariantId
        ? list.querySelector(`input[value="${preferredVariantId}"]:not(:disabled)`)
        : previousSizeLabel
          ? Array.from(list.querySelectorAll('input:not(:disabled)')).find(
            (input) => normalize(input.dataset.size) === normalize(previousSizeLabel)
          )
          : null;
      const defaultRadio = preferredRadio
        || list.querySelector('input:not(:disabled)')
        || list.querySelector('input');

      if (defaultRadio && !defaultRadio.disabled) {
        defaultRadio.checked = true;
        setSelectedSize(side, defaultRadio);
      }
    }

    function setSelectedSize(side, radio) {
      if (!radio || radio.disabled) return;

      state[side].variantId = Number(radio.value);
      state[side].price = Number(radio.dataset.price || 0);

      els.sizeList[side]?.querySelectorAll('.f-button__list__item').forEach((item) => {
        item.classList.toggle('f-button__list__item--active', item.contains(radio));
      });

      updateMainPrice();
    }

    function setSwatchSelection(side, productId) {
      els.swatches[side].forEach((swatch) => {
        const isSelected = String(swatch.dataset.productId) === String(productId);
        swatch.classList.toggle('is-selected', isSelected);
      });
    }

    function findProduct(side, productId) {
      const products = side === 'top' ? data.tops : data.bottoms;
      return products.find((product) => String(product.id) === String(productId)) || null;
    }

    function selectProduct(side, product, preferredVariantId) {
      if (!product) return;

      if (side === 'top') state.topProduct = product;
      else state.bottomProduct = product;

      setSwatchSelection(side, product.id);
      renderSizes(side, product, preferredVariantId);
      updateMainPrice();
    }

    function imageUrl(src, width) {
      if (!src) return '';
      const url = new URL(src.startsWith('//') ? `https:${src}` : src);
      url.searchParams.set('width', width);
      return url.toString();
    }

    function buildSrcset(image) {
      return imageWidths
        .filter((width) => !image.width || width <= image.width)
        .map((width) => `${imageUrl(image.src, width)} ${width}w`)
        .join(', ');
    }

    function buildGallerySlide(image, index, productTitle, enableZoom) {
      const aspectRatio = image.width && image.height ? image.width / image.height : 1;
      const slide = document.createElement('li');
      slide.dataset.swiperSlide = '';
      slide.dataset.mediaId = image.id;
      slide.className = 'swiper-slide';
      slide.style.setProperty('--slide-ratio', aspectRatio);

      let content = slide;
      if (enableZoom) {
        const link = document.createElement('a');
        link.href = imageUrl(image.src, 4096);
        link.dataset.pswpWidth = image.width || 4096;
        link.dataset.pswpHeight = image.height || 4096;
        link.target = '_blank';
        link.tabIndex = 0;
        slide.appendChild(link);
        content = link;
      }

      const img = document.createElement('img');
      img.srcset = buildSrcset(image);
      img.sizes = '100vw';
      img.src = imageUrl(image.src, 500);
      img.alt = image.alt || productTitle || '';
      img.width = 1100;
      img.height = Math.ceil(1100 / aspectRatio);
      img.dataset.mediaId = image.id;
      img.className = `wt-product__img${enableZoom ? ' wt-product__img--zoom-cursor' : ''}`;
      if (index > 0) img.loading = 'lazy';
      content.appendChild(img);

      return slide;
    }

    function buildThumbSlide(image, productTitle) {
      const aspectRatio = image.width && image.height ? image.width / image.height : 1;
      const slide = document.createElement('li');
      slide.dataset.swiperSlide = '';
      slide.className = 'thumbs-list-item';
      slide.dataset.slideMediaId = image.id;
      slide.style.setProperty('--thumb-ratio', aspectRatio);
      slide.tabIndex = -1;

      const wrapper = document.createElement('div');
      const img = document.createElement('img');
      img.src = imageUrl(image.src, 300);
      img.alt = image.alt || productTitle || '';
      img.loading = 'lazy';
      img.width = 300;
      img.height = Math.ceil(300 / aspectRatio);
      img.dataset.mediaId = image.id;
      img.className = 'wt-product__img';
      wrapper.appendChild(img);
      slide.appendChild(wrapper);

      return slide;
    }

    async function fetchProduct(handle) {
      if (productCache.has(handle)) return productCache.get(handle);

      const rootUrl = block.dataset.rootUrl || '/';
      const baseUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
      const response = await fetch(`${baseUrl}products/${handle}.js`);
      if (!response.ok) throw new Error(`Failed to load product: ${handle}`);

      const product = await response.json();
      productCache.set(handle, product);
      return product;
    }

    async function updateGallery(activeHandle) {
      await customElements.whenDefined('gallery-section');
      const gallery = document.querySelector('gallery-section[id^="MediaGallery-"]');
      if (!gallery?.elements || typeof gallery.filterSlides !== 'function') return;

      const topHandle = state.topProduct?.handle;
      const bottomHandle = state.bottomProduct?.handle;

      try {
        const [topProduct, bottomProduct] = await Promise.all([
          topHandle ? fetchProduct(topHandle) : null,
          bottomHandle ? fetchProduct(bottomHandle) : null
        ]);

        // /products/{handle}.js returns `images` as plain URL strings; the real
        // media objects (with id/width/height) live on `media`.
        const collectImages = (fetchedProduct) => (fetchedProduct?.media || [])
          .filter((item) => item.media_type === 'image')
          .map((media) => ({ product: fetchedProduct, media }));

        const combined = [...collectImages(topProduct), ...collectImages(bottomProduct)];
        if (!combined.length) return;

        const enableZoom = gallery.querySelector('[data-gallery]')?.classList.contains('photoswipe');

        // Swap the gallery's own source slides, then let its existing rendering
        // logic (swiper or masonry/collage) rebuild via filterSlides.
        gallery.elements.gallerySlides = combined.map((entry, index) => (
          buildGallerySlide(entry.media, index, entry.product.title, enableZoom)
        ));
        gallery.elements.thumbsSlides = combined.map((entry) => (
          buildThumbSlide(entry.media, entry.product.title)
        ));

        const active = combined.find((entry) => entry.product.handle === activeHandle) || combined[0];
        gallery.filterSlides([], active.media.id, false);
      } catch (error) {
        console.error('Mix match gallery update failed:', error);
      }
    }

    function onSwatchClick(side, swatch) {
      if (swatch.disabled || swatch.classList.contains('disabled')) return;

      const product = findProduct(side, swatch.dataset.productId);
      if (!product) return;

      selectProduct(side, product);
      updateGallery(product.handle);
    }

    function selectColor(color, topProductId, bottomProductId, preferredTopVariantId, preferredBottomVariantId) {
      state.color = color || null;
      state.topProduct = data.tops.find((product) => String(product.id) === String(topProductId))
        || data.tops.find((product) => hasColor(product, color))
        || firstAvailableProduct(data.tops);
      state.bottomProduct = data.bottoms.find((product) => String(product.id) === String(bottomProductId))
        || data.bottoms.find((product) => hasColor(product, color))
        || firstAvailableProduct(data.bottoms);

      els.colorInputs.forEach((input) => {
        input.checked = normalize(input.dataset.color) === normalize(color);
      });

      selectProduct('top', state.topProduct, preferredTopVariantId);
      selectProduct('bottom', state.bottomProduct, preferredBottomVariantId);
      updateGallery(state.topProduct?.handle);
    }

    function initLowestCombination() {
      const lowest = lowestCombination();
      if (!lowest) {
        if (els.colorOption) els.colorOption.hidden = true;
        selectProduct('top', firstAvailableProduct(data.tops));
        selectProduct('bottom', firstAvailableProduct(data.bottoms));
        return;
      }

      const matchingColorInput = els.colorInputs.find((input) => (
        String(input.dataset.topProductId) === String(lowest.top.id)
        && String(input.dataset.bottomProductId) === String(lowest.bottom.id)
      ));

      if (matchingColorInput) {
        state.color = matchingColorInput.dataset.color;
        els.colorInputs.forEach((input) => {
          input.checked = normalize(input.dataset.color) === normalize(matchingColorInput.dataset.color);
        });
      } else {
        state.color = null;
        els.colorInputs.forEach((input) => {
          input.checked = false;
        });
      }

      selectProduct('top', lowest.top, lowest.topVariant.id);
      selectProduct('bottom', lowest.bottom, lowest.bottomVariant.id);
    }

    function formatWithDelimiters(cents, precision, thousands, decimal) {
      const fixed = (Number(cents || 0) / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    function formatMoney(cents, format) {
      const formatString = format || '${{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholder);

      if (match) {
        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          default:
            value = formatWithDelimiters(cents, 2, ',', '.');
        }
        return formatString.replace(placeholder, value);
      }

      if (block.dataset.currencyCode && typeof Intl !== 'undefined') {
        try {
          return new Intl.NumberFormat(block.dataset.languageCode || undefined, {
            style: 'currency',
            currency: block.dataset.currencyCode
          }).format(Number(cents || 0) / 100);
        } catch (error) {
          // fall through to plain amount
        }
      }

      return formatWithDelimiters(cents, 2, ',', '.');
    }

    function setPriceText(element, formattedPrice) {
      if (!element) return;
      const amountNode = element.classList.contains('price-amount')
        ? element
        : element.querySelector('.price-amount');
      (amountNode || element).textContent = formattedPrice;
    }

    function updateMainPrice() {
      const displayPrice = Number(state.top.price || 0) + Number(state.bottom.price || 0);
      if (displayPrice <= 0) return;

      const formattedPrice = formatMoney(displayPrice, config.moneyFormat);
      const priceBlock = document.querySelector('.wd-wt-product__price[id^="price-"]')
        || document.querySelector('.wt-product__price.wd-wt-product__price');

      if (priceBlock) {
        const salePrice = priceBlock.querySelector('.price-item--sale.wt-product__price__final');
        const regularPrice = priceBlock.querySelector('.price__regular .wt-product__price__final');
        setPriceText(salePrice || regularPrice, formattedPrice);
      }

      setPriceText(document.querySelector('.wd-wt-product__price_mobile'), formattedPrice);
      setPriceText(document.querySelector('.wt-product__sticky-buy__price'), formattedPrice);
      updateStockUi();
    }

    function updateStockUi() {
      const complete = isComplete();
      const waitlist = document.querySelector('#sealapps-bis-widget');
      const inventory = document.querySelector('.product__inventory');
      const addToCartWrapper = document.querySelector('.wt-product__add-to-cart_form--wrapper');

      if (complete && inventory && addToCartWrapper && inventory.parentElement !== addToCartWrapper) {
        addToCartWrapper.insertBefore(inventory, addToCartWrapper.firstChild);
      }

      if (waitlist) {
        waitlist.style.display = complete ? 'none' : '';
      }

      if (inventory) {
        inventory.classList.remove('visibility-hidden');
        inventory.style.display = '';
        const statusText = inventory.querySelector('span');
        if (statusText && complete) {
          const inStockText = window.variantStrings?.inStock || statusText.dataset.mixMatchInStock || statusText.textContent;
          statusText.dataset.mixMatchInStock = inStockText;
        }
      }

      if (mainBtn) {
        mainBtn.disabled = !complete;
      }
    }

    function isComplete() {
      if (!state.top.variantId || !state.bottom.variantId) return false;

      const topRadio = block.querySelector(`[data-size-list="top"] input[value="${state.top.variantId}"]`);
      const bottomRadio = block.querySelector(`[data-size-list="bottom"] input[value="${state.bottom.variantId}"]`);

      return Boolean(
        topRadio
        && bottomRadio
        && !topRadio.disabled
        && !bottomRadio.disabled
      );
    }

    function generateSetId() {
      return `set_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }

    async function addToCart(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!isComplete()) return;

      const buttonText = mainBtn?.querySelector('span');
      const originalText = buttonText?.textContent || '';

      if (mainBtn) mainBtn.disabled = true;
      if (buttonText) buttonText.textContent = 'Adding...';

      try {
        const setId = generateSetId();
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              { id: state.top.variantId, quantity: 1, properties: { [SET_PROPERTY]: setId, _mix_match_side: 'top' } },
              { id: state.bottom.variantId, quantity: 1, properties: { [SET_PROPERTY]: setId, _mix_match_side: 'bottom' } }
            ]
          })
        });

        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error(payload.description || 'Failed to add mix match set');

        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
        const cartTrigger = document.querySelector('.wt-cart__trigger');
        if (cartTrigger) {
          setTimeout(() => {
            cartTrigger.click();
            setTimeout(() => document.dispatchEvent(new CustomEvent('cart-drawer:refresh')), 400);
          }, 250);
        }
      } catch (error) {
        console.error('Add to cart error:', error);
      } finally {
        if (mainBtn) mainBtn.disabled = false;
        if (buttonText) buttonText.textContent = originalText;
      }
    }

    els.colorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        selectColor(input.dataset.color, input.dataset.topProductId, input.dataset.bottomProductId);
      });
    });

    ['top', 'bottom'].forEach((side) => {
      els.swatches[side].forEach((swatch) => {
        swatch.addEventListener('click', () => onSwatchClick(side, swatch));
      });
    });

    ['top', 'bottom'].forEach((side) => {
      els.sizeList[side]?.addEventListener('change', (event) => {
        if (event.target?.type === 'radio') setSelectedSize(side, event.target);
      });
    });

    if (mainForm) mainForm.addEventListener('submit', addToCart, true);
    if (mainBtn) mainBtn.addEventListener('click', addToCart, true);

    initLowestCombination();
    updateGallery(state.topProduct?.handle);

    setTimeout(updateStockUi, 500);
    setTimeout(updateStockUi, 1500);
  }

  function initAll() {
    document.querySelectorAll('.wd-mix-match-picker[data-block-id]').forEach(initMixMatchPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList?.contains('wd-mix-match-picker')) {
            initMixMatchPicker(node);
            return;
          }

          const picker = node.querySelector?.('.wd-mix-match-picker[data-block-id]');
          if (picker) initMixMatchPicker(picker);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/**
 * Mix & Match Cart Sync
 * - If one item is deleted, delete the paired item
 * - Quantity changes are blocked in the cart UI for mix-match items
 */
(function () {
  'use strict';

  const SET_PROPERTY = '_mix_match_set';
  let isSyncing = false;

  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      return response.ok ? response.json() : null;
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  async function removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  }

  function getSetItems(cart, setId) {
    return cart.items.filter((item) => item.properties?.[SET_PROPERTY] === setId);
  }

  function getSetIds(cart) {
    const ids = new Set();
    cart.items.forEach((item) => {
      if (item.properties?.[SET_PROPERTY]) ids.add(item.properties[SET_PROPERTY]);
    });
    return Array.from(ids);
  }

  async function syncSets() {
    if (isSyncing) return;
    isSyncing = true;

    try {
      const cart = await getCart();
      if (!cart) return;

      let changed = false;

      for (const setId of getSetIds(cart)) {
        const setItems = getSetItems(cart, setId);

        if (setItems.length === 1) {
          await removeItem(setItems[0].key);
          changed = true;
        }
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
        document.querySelector('cart-items.cart-page')?.onCartUpdate?.();
      }
    } finally {
      setTimeout(() => {
        isSyncing = false;
      }, 500);
    }
  }

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const result = await originalFetch.apply(this, args);
    const url = args[0]?.toString() || '';

    if (url.includes('/cart/change') || url.includes('/cart/update')) {
      setTimeout(syncSets, 100);
    }

    return result;
  };

  document.addEventListener('cart-drawer:refresh', () => {
    setTimeout(syncSets, 300);
  });
  document.addEventListener('cart:updated', syncSets);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncSets);
  } else {
    syncSets();
  }
})();
